import { useEffect, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { LocaleDetail } from '../../components/locali'
import { MapContainer } from '../../components/map'
import { LocaleDetailSkeleton } from '../../components/common/Skeleton'
import { Button } from '../../components/common'
import { useLocaliStore, useFavoritiStore } from '../../store'
import { incrementaVisite } from '../../services/locali.service'
import { useSEO } from '../../hooks'

/**
 * Pagina dettaglio singolo locale
 */
export default function LocalePage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()

  // Store per locali e favoriti
  const { localeCorrente, locali, loading, fetchLocaleBySlug, fetchLocali, clearLocaleCorrente } = useLocaliStore()
  const { isFavorito, toggleFavorito, loadFavoriti } = useFavoritiStore()

  // Carica locale e favoriti all'avvio
  useEffect(() => {
    if (slug) {
      fetchLocaleBySlug(slug)
      loadFavoriti()
      // Incrementa visite
      incrementaVisite(slug)
    }

    // Cleanup quando si lascia la pagina
    return () => {
      clearLocaleCorrente()
    }
  }, [slug])

  // Carica altri locali della stessa provincia
  useEffect(() => {
    if (localeCorrente && locali.length === 0) {
      fetchLocali({ provincia: localeCorrente.provincia })
    }
  }, [localeCorrente])

  // SEO dinamico
  useSEO({
    title: localeCorrente?.nome,
    description: localeCorrente
      ? `${localeCorrente.tipo.charAt(0).toUpperCase() + localeCorrente.tipo.slice(1)} a ${localeCorrente.comune} (${localeCorrente.provincia}). ${localeCorrente.specialita?.slice(0, 3).join(', ') || ''} - Approvato dal Conero Garage`
      : undefined,
    image: localeCorrente?.fotoPrincipale
      ? `https://api.conerogarage.it${localeCorrente.fotoPrincipale}`
      : undefined,
    url: localeCorrente ? `https://approved.conerogarage.it/locale/${localeCorrente.slug}` : undefined,
  })

  // Altri locali nella stessa provincia
  const altriLocali = useMemo(() => {
    if (!localeCorrente) return []
    return locali
      .filter(l => l.provincia === localeCorrente.provincia && l.id !== localeCorrente.id)
      .slice(0, 3)
  }, [locali, localeCorrente])

  // Locale non trovato
  if (!localeCorrente && !loading) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="font-display text-3xl text-white mb-4">
            Locale non trovato
          </h1>
          <p className="text-gray-400 mb-8">
            Il locale che stai cercando non esiste o potrebbe essere stato rimosso.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="primary" onClick={() => navigate('/locali')}>
              Vedi tutti i locali
            </Button>
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Torna indietro
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (loading || !localeCorrente) {
    return (
      <div className="container mx-auto px-4 py-8">
        <LocaleDetailSkeleton />
      </div>
    )
  }

  const isLocaleFavorito = isFavorito(localeCorrente.id)

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav className="mb-6 text-sm">
        <ol className="flex items-center gap-2 text-gray-400">
          <li>
            <Link to="/" className="hover:text-gold transition-colors">
              Home
            </Link>
          </li>
          <li>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </li>
          <li>
            <Link to="/locali" className="hover:text-gold transition-colors">
              Locali
            </Link>
          </li>
          <li>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </li>
          <li className="text-gold truncate max-w-[200px]">
            {localeCorrente.nome}
          </li>
        </ol>
      </nav>

      {/* Dettaglio locale */}
      <LocaleDetail
        locale={localeCorrente}
        isFavorito={isLocaleFavorito}
        onFavoritoClick={() => toggleFavorito(localeCorrente.id)}
      />

      {/* Mappa posizione */}
      <section className="mt-12">
        <h2 className="font-display text-2xl text-white mb-6">Dove siamo</h2>
        <MapContainer
          locali={[localeCorrente]}
          selectedLocaleId={localeCorrente.id}
          height="300px"
        />
      </section>

      {/* Altri locali nella zona */}
      {altriLocali.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-2xl text-white mb-6">
            Altri locali in provincia di {localeCorrente.provincia}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {altriLocali.map(l => (
              <Link
                key={l.id}
                to={`/locale/${l.slug}`}
                className="card p-4 hover:border-gold/50 transition-colors"
              >
                <h3 className="font-display text-lg text-white mb-1 truncate">
                  {l.nome}
                </h3>
                <p className="text-sm text-gray-400">
                  {l.comune}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
